import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore from '../store/useAuthStore';
import useNotificationStore from '../store/useNotificationStore';

const SOCKET_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:4000';

export const useSocket = () => {
  const socketRef = useRef(null);
  const queryClient = useQueryClient();
  const { token, isAuthenticated } = useAuthStore();
  const { addNotification, incrementUnreadCount } = useNotificationStore();
  const [isConnected, setIsConnected] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);

  // Initialize socket connection
  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    // Create socket connection
    socketRef.current = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      console.log('🔌 Socket connected');
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setIsConnected(false);
    });

    // Handle notifications
    socket.on('notification:new', (notification) => {
      console.log('📬 New notification:', notification);
      addNotification(notification);
      incrementUnreadCount();

      // Invalidate React Query cache to update UI immediately
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Show browser notification if permitted
      if (Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
        });
      }
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [isAuthenticated, token, addNotification, incrementUnreadCount, queryClient]);

  // Join project room
  const joinProject = useCallback((projectId) => {
    if (socketRef.current && projectId) {
      // Leave previous project room
      if (currentProject && currentProject !== projectId) {
        socketRef.current.emit('leave:project', currentProject);
      }
      socketRef.current.emit('join:project', projectId);
      setCurrentProject(projectId);
    }
  }, [currentProject]);

  // Leave project room
  const leaveProject = useCallback((projectId) => {
    if (socketRef.current && projectId) {
      socketRef.current.emit('leave:project', projectId);
      if (currentProject === projectId) {
        setCurrentProject(null);
      }
    }
  }, [currentProject]);

  // Subscribe to specific events
  const on = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
      }
    };
  }, []);

  // Unsubscribe from events
  const off = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.off(event, callback);
    }
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    joinProject,
    leaveProject,
    on,
    off,
  };
};

export default useSocket;
